/**
 * Micro-slot op het object dat je in het attributenpaneel bewerkt.
 *
 * Zie `docs/architecture/gelijktijdigheid.md` §5. Het slot wordt gepakt zodra
 * de focus in een veld belandt en losgelaten kort nadat 'ie het paneel verlaat
 * — niet zodra je een object aanklikt, want dan zou iedereen die iets
 * selecteert het voor een ander op slot zetten.
 *
 * Het claimen zelf woont in `state/objectLocks.ts`, want het canvas doet
 * hetzelfde bij F2 en dubbelklik. Twee deuren naar hetzelfde veld, één slot.
 *
 * Er is geen indienen. Het slot leeft zolang je bezig bent en valt daarna
 * vanzelf; de serverkant laat 'm sowieso vervallen als er niets meer
 * binnenkomt.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { claimObjectLock, onLockLost, releaseObjectLock } from "../../state/objectLocks";

/** Wachten na het verlaten van een veld voordat we vrijgeven. Tussen twee
 *  velden in hetzelfde paneel springen mag het slot niet opheffen. */
const RELEASE_DELAY_MS = 1_500;
/** Opnieuw proberen zolang een ander het vasthoudt. Zonder dit blijft het
 *  paneel dicht nadat de ander klaar is: de velden staan uit, dus er komt
 *  geen focus meer die een nieuwe poging zou uitlokken. */
const RETRY_MS = 5_000;

export interface ObjectLockState {
  /** Je hád het slot en bent 't kwijtgeraakt terwijl je bezig was. */
  lost: boolean;
  /** Iemand anders heeft dit object vast; bewerken hoort uit te staan. */
  heldBy: string | null;
  /** Vastgehouden vanwege een import of restore in plaats van een collega. */
  byBatch: boolean;
  /** Focus kwam in een veld. */
  begin: () => void;
  /** Focus verliet een veld — met uitstel, zie RELEASE_DELAY_MS. */
  endSoon: () => void;
}

export function useObjectLock(objectId: string | null): ObjectLockState {
  const [heldBy, setHeldBy] = useState<string | null>(null);
  const [byBatch, setByBatch] = useState(false);
  const [lost, setLost] = useState(false);
  const mine = useRef(false);
  const wanted = useRef(false);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retry = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    wanted.current = false;
    if (retry.current) {
      clearInterval(retry.current);
      retry.current = null;
    }
    if (!mine.current || !objectId) return;
    mine.current = false;
    releaseObjectLock(objectId);
  }, [objectId]);

  // Wisselen van object: het vorige slot hoort meteen los, en de melding
  // erover mag niet blijven staan boven het volgende object.
  useEffect(() => {
    setHeldBy(null);
    setByBatch(false);
    return stop;
  }, [stop]);

  // Slot kwijt terwijl je bezig bent — verlopen of overgenomen door een
  // batch. Dat hoort je te bereiken vóór je opslaat, niet erna.
  useEffect(() => {
    onLockLost((verloren) => {
      if (verloren !== objectId) return;
      mine.current = false;
      setLost(true);
    });
    return () => onLockLost(null);
  }, [objectId]);

  const claim = useCallback(() => {
    if (!objectId || mine.current) return;
    void claimObjectLock(objectId).then((denial) => {
      if (denial) {
        setHeldBy(denial.holder);
        setByBatch(denial.byBatch);
        if (!retry.current) {
          retry.current = setInterval(() => {
            if (wanted.current) claim();
          }, RETRY_MS);
        }
        return;
      }
      if (retry.current) {
        clearInterval(retry.current);
        retry.current = null;
      }
      setHeldBy(null);
      setByBatch(false);
      mine.current = true;
      // Ondertussen kan de gebruiker het paneel al verlaten hebben; dan is dit
      // slot meteen weer overbodig.
      if (!wanted.current) stop();
    });
  }, [objectId, stop]);

  const begin = useCallback(() => {
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = null;
    }
    wanted.current = true;
    claim();
  }, [claim]);

  const endSoon = useCallback(() => {
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(() => {
      releaseTimer.current = null;
      stop();
    }, RELEASE_DELAY_MS);
  }, [stop]);

  return { heldBy, byBatch, lost, begin, endSoon };
}
